import { useCallback, useEffect, useState } from 'react';
import { fechaNegocio, formatearFecha, type Producto } from '@tanteo/compartido';
import { listarProductos } from '../../lib/api';
import {
  cambiarEstadoDescuentoNegocio,
  eliminarDescuentoNegocio,
  guardarDescuentoNegocio,
  listarDescuentosNegocio,
  type DescuentoDelNegocio,
} from '../../lib/api-descuentos';
import { EncabezadoTabla, FilaTabla, Insignia, MensajeError, TablaAncha, Tarjeta, ThAcciones } from '../../ui/base';
import { BotonesFila, ConfirmarEliminar, EncabezadoSeccion } from './partes';
import { alcanceDe, estadoDe, valorDe, vigenciaDe } from './textosDescuento';
import { VentanaDescuento } from './VentanaDescuento';

type Edicion = { descuento: DescuentoDelNegocio | null } | null;

function lugaresDe(d: DescuentoDelNegocio, nombreDe: (id: string) => string): string {
  const productos = d.alcance.length > 0 ? alcanceDe(d.alcance, nombreDe) : 'todos';
  if (d.enProducto && d.enVenta) return 'Productos y venta';
  if (d.enVenta) return 'Al total de la venta';
  return d.automatico ? `Productos · ${productos} · solo` : `Productos · ${productos}`;
}

function rolesDe(d: DescuentoDelNegocio): string {
  if (d.automatico) return 'Todos';
  return [d.paraPropietario && 'Propietario', d.paraCajero && 'Cajero'].filter(Boolean).join(', ');
}

function Estado({ d, hoy }: { d: DescuentoDelNegocio; hoy: string }) {
  const estado = estadoDe(d, hoy);
  if (estado === 'activo') return <Insignia tono="exito">Activo</Insignia>;
  if (estado === 'programado') return <Insignia tono="info">Programado · empieza el {formatearFecha(d.desde ?? '').slice(0, 5)}</Insignia>;
  return <Insignia tono="neutro">{estado === 'vencido' ? 'Vencido' : 'Inactivo'}</Insignia>;
}

/** Los descuentos que el mostrador ofrece: solo los activos, y a cada quien los que tiene marcados. */
export function SeccionDescuentos({ negocioId }: { negocioId: string }) {
  const [descuentos, setDescuentos] = useState<DescuentoDelNegocio[]>([]);
  const [productos, setProductos] = useState<Producto[]>([]);
  const [edicion, setEdicion] = useState<Edicion>(null);
  const [aEliminar, setAEliminar] = useState<DescuentoDelNegocio | null>(null);
  const [aviso, setAviso] = useState('');
  const [errorEliminar, setErrorEliminar] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const cargar = useCallback(() => {
    listarDescuentosNegocio(negocioId)
      .then(setDescuentos)
      .catch(() => setAviso('No pudimos cargar los descuentos.'));
  }, [negocioId]);
  useEffect(cargar, [cargar]);
  useEffect(() => {
    listarProductos(negocioId)
      .then(setProductos)
      .catch(() => setAviso('No pudimos cargar los productos.'));
  }, [negocioId]);
  const hoy = fechaNegocio();
  const nombreDeProducto = (id: string) => productos.find((p) => p.id === id)?.nombre ?? 'Producto';

  async function hacer(accion: () => Promise<void>, alFallar: (m: string) => void): Promise<boolean> {
    setOcupado(true);
    try {
      await accion();
      cargar();
      return true;
    } catch (e) {
      alFallar(e instanceof Error ? e.message : 'No se pudo guardar.');
      return false;
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Tarjeta>
      <EncabezadoSeccion
        titulo="Descuentos"
        ayuda="Al total de la venta se usa con F8; a productos, con el % de cada línea. A productos, con fechas o días es automático: se aplica solo. El nombre sale en el comprobante."
        agregar="Agregar descuento"
        onAgregar={() => setEdicion({ descuento: null })}
        e2eAgregar="nuevo-descuento"
      />
      <TablaAncha data-e2e="lista-descuentos">
        <EncabezadoTabla>
          <th className="py-2 text-left">Nombre</th>
          <th className="py-2 text-left">Valor</th>
          <th className="py-2 text-left">Vigencia</th>
          <th className="py-2 text-left">Dónde se usa</th>
          <th className="py-2 text-left">Quién puede darlo</th>
          <th className="py-2 text-left">Estado</th>
          <ThAcciones />
        </EncabezadoTabla>
        <tbody>
          {descuentos.map((d) => (
            <FilaTabla key={d.id}>
              <td className={`py-2 pr-2 font-semibold ${d.activo ? '' : 'text-muted'}`}>{d.nombre}</td>
              <td className={`py-2 pr-2 ${d.forma === 'libre' || !d.activo ? 'text-muted' : ''}`}>{valorDe(d)}</td>
              <td className={`py-2 pr-2 ${d.activo ? '' : 'text-muted'}`} data-e2e={`vigencia-${d.nombre}`}>
                {vigenciaDe(d)}
              </td>
              <td className={`py-2 pr-2 ${d.activo ? '' : 'text-muted'}`}>{lugaresDe(d, nombreDeProducto)}</td>
              <td className={`py-2 pr-2 ${d.activo ? '' : 'text-muted'}`}>{rolesDe(d)}</td>
              <td className="py-2 pr-2">
                <Estado d={d} hoy={hoy} />
              </td>
              <td className="py-2">
                <BotonesFila
                  nombre={d.nombre}
                  activo={d.activo}
                  ocupado={ocupado}
                  onEditar={() => setEdicion({ descuento: d })}
                  onEstado={() => void hacer(() => cambiarEstadoDescuentoNegocio(negocioId, d.id, !d.activo), setAviso)}
                  onEliminar={() => {
                    setErrorEliminar('');
                    setAEliminar(d);
                  }}
                />
              </td>
            </FilaTabla>
          ))}
        </tbody>
      </TablaAncha>
      <MensajeError>{aviso}</MensajeError>

      <VentanaDescuento
        edicion={edicion}
        descuentos={descuentos}
        productos={productos}
        ocupado={ocupado}
        onCerrar={() => setEdicion(null)}
        onGuardar={(datos, alFallar) => {
          const descuento = edicion?.descuento ?? null;
          void hacer(
            () => guardarDescuentoNegocio(negocioId, descuento?.id ?? null, datos),
            alFallar
          ).then((ok) => ok && setEdicion(null));
        }}
      />
      <ConfirmarEliminar
        titulo={aEliminar ? `¿Eliminar el descuento «${aEliminar.nombre}»?` : null}
        puntos={['Deja de salir en el mostrador.', 'Los comprobantes ya emitidos conservan el descuento con el que se vendió.']}
        ocupado={ocupado}
        error={errorEliminar}
        onCerrar={() => setAEliminar(null)}
        onDesactivar={
          aEliminar?.activo
            ? () =>
                void hacer(() => cambiarEstadoDescuentoNegocio(negocioId, aEliminar.id, false), setErrorEliminar).then(
                  (ok) => ok && setAEliminar(null)
                )
            : undefined
        }
        onEliminar={() =>
          aEliminar &&
          void hacer(() => eliminarDescuentoNegocio(negocioId, aEliminar.id), setErrorEliminar).then(
            (ok) => ok && setAEliminar(null)
          )
        }
      />
    </Tarjeta>
  );
}
